'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BmrCalculator() {
  const t = useTranslations("tools.bmrCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    weightLbs: 160,
    heightCm: 178,
    weightKg: 73,
    formula: 'mifflin' as 'mifflin' | 'harris' | 'katch',
    bodyfat: 20,
  });
  const [results, setResults] = useState<{
    bmr: number;
    tdee: Array<{ level: string; calories: number }>;
  } | null>(null);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    calculatorLayout: {
      display: 'grid',
      gap: '2rem',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    unitTabs: {
      display: 'flex',
      gap: '0.5rem',
      background: '#f1f5f9',
      padding: '0.25rem',
      borderRadius: '0.5rem',
    },
    unitTab: {
      flex: 1,
      padding: '0.5rem 1rem',
      border: 'none',
      background: 'transparent',
      color: '#64748b',
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.2s',
    },
    unitTabActive: {
      background: 'white',
      color: '#2563eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    formRow: {
      display: 'grid',
      gap: '1rem',
    },
    formGroup: {
      marginBottom: '1.25rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#334155',
      fontWeight: 500,
      fontSize: '0.9rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.2s',
      boxSizing: 'border-box' as const,
    },
    inputWithUnit: {
      position: 'relative' as const,
    },
    inputUnit: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontSize: '0.9rem',
      fontWeight: 500,
    },
    heightInputs: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.5rem',
    },
    genderOptions: {
      display: 'flex',
      gap: '1rem',
    },
    radioOption: {
      flex: 1,
      position: 'relative' as const,
    },
    radioLabel: {
      display: 'block',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      cursor: 'pointer',
      transition: 'all 0.2s',
      margin: 0,
    },
    radioLabelActive: {
      borderColor: '#2563eb',
      background: '#eff6ff',
      color: '#2563eb',
      fontWeight: 600,
    },
    advancedSettings: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #e2e8f0',
    },
    settingsToggle: {
      background: 'none',
      border: 'none',
      color: '#2563eb',
      fontWeight: 600,
      cursor: 'pointer',
      padding: 0,
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    calculateButton: {
      width: '100%',
      padding: '1rem',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '1rem',
    },
    bmrResult: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    bmrValue: {
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: '0.25rem',
    },
    tdeeTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '1rem',
    },
    tdeeTableTh: {
      background: '#f8fafc',
      padding: '0.75rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.85rem',
      borderBottom: '2px solid #e2e8f0',
    },
    tdeeTableTd: {
      padding: '0.75rem',
      borderBottom: '1px solid #e2e8f0',
      color: '#334155',
    },
    infoNote: {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
      fontSize: '0.85rem',
      color: '#78350f',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    h3: {
      fontWeight: 600,
      color: colors.textPrimary,
      fontSize: '1.2rem',
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.7,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
    },
    reference: {
      marginBottom: '0.75rem',
      paddingLeft: '1rem',
      borderLeft: `3px solid ${colors.primary}`,
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightCm: number;
    let weightKg: number;

    if (unitSystem === 'imperial') {
      heightCm = ((inputs.feet * 12) + inputs.inches) * 2.54;
      weightKg = inputs.weightLbs * 0.453592;
    } else {
      heightCm = inputs.heightCm;
      weightKg = inputs.weightKg;
    }

    let bmr: number;

    if (inputs.formula === 'mifflin') {
      if (inputs.gender === 'male') {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) + 5;
      } else {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) - 161;
      }
    } else if (inputs.formula === 'harris') {
      if (inputs.gender === 'male') {
        bmr = 13.397 * weightKg + 4.799 * heightCm - 5.677 * inputs.age + 88.362;
      } else {
        bmr = 9.247 * weightKg + 3.098 * heightCm - 4.330 * inputs.age + 447.593;
      }
    } else {
      const leanMass = weightKg * (1 - inputs.bodyfat / 100);
      bmr = 370 + (21.6 * leanMass);
    }

    bmr = Math.round(bmr);

    const activityLevels = [
      { level: t('activitySedentary'), multiplier: 1.2 },
      { level: t('activityLight'), multiplier: 1.375 },
      { level: t('activityModerate'), multiplier: 1.465 },
      { level: t('activityActive'), multiplier: 1.55 },
      { level: t('activityVeryActive'), multiplier: 1.725 },
      { level: t('activityExtraActive'), multiplier: 1.9 },
    ];

    const tdee = activityLevels.map(level => ({
      level: level.level,
      calories: Math.round(bmr * level.multiplier),
    }));

    setResults({ bmr, tdee });
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem', marginBottom: '1rem' },
    { fontSize: '2.5rem', marginBottom: '1.25rem' },
    { fontSize: '3rem', marginBottom: '1.5rem' }
  );

  const summaryStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem', marginBottom: '2rem' },
    { padding: '2rem 1.5rem', marginBottom: '2.5rem' },
    { padding: '3rem 2rem', marginBottom: '3rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '1000px' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    baseStyles.calculatorLayout,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const formRowStyle = getResponsiveStyle(
    baseStyles.formRow,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const bmrValueStyle = getResponsiveStyle(
    baseStyles.bmrValue,
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={{ ...h2Style, marginBottom: '0.5rem' }}>{t('toolTitle')}</h2>
            <p style={{ color: '#64748b', fontSize: '1rem', margin: 0 }}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={{ ...baseStyles.glassCard, padding: '2rem' }}>
              <div style={baseStyles.unitTabs}>
                <button
                  type="button"
                  style={{
                    ...baseStyles.unitTab,
                    ...(unitSystem === 'imperial' ? baseStyles.unitTabActive : {}),
                  }}
                  onClick={() => setUnitSystem('imperial')}
                >
                  {t('unitImperial')}
                </button>
                <button
                  type="button"
                  style={{
                    ...baseStyles.unitTab,
                    ...(unitSystem === 'metric' ? baseStyles.unitTabActive : {}),
                  }}
                  onClick={() => setUnitSystem('metric')}
                >
                  {t('unitMetric')}
                </button>
              </div>

              <form onSubmit={handleCalculate}>
                <div style={formRowStyle}>
                  <div style={baseStyles.formGroup}>
                    <label style={baseStyles.label}>{t('labelAge')}</label>
                    <div style={baseStyles.inputWithUnit}>
                      <input
                        type="number"
                        style={baseStyles.input}
                        min="15"
                        max="80"
                        value={inputs.age}
                        onChange={(e) => setInputs({ ...inputs, age: parseInt(e.target.value) })}
                        required
                      />
                      <span style={baseStyles.inputUnit}>{t('unitYears')}</span>
                    </div>
                  </div>

                  <div style={baseStyles.formGroup}>
                    <label style={baseStyles.label}>{t('labelGender')}</label>
                    <div style={baseStyles.genderOptions}>
                      <div style={baseStyles.radioOption}>
                        <input
                          type="radio"
                          id="male"
                          name="gender"
                          value="male"
                          checked={inputs.gender === 'male'}
                          onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'male' | 'female' })}
                          style={{ position: 'absolute', opacity: 0 }}
                        />
                        <label
                          htmlFor="male"
                          style={{
                            ...baseStyles.radioLabel,
                            ...(inputs.gender === 'male' ? baseStyles.radioLabelActive : {}),
                          }}
                        >
                          {t('genderMale')}
                        </label>
                      </div>
                      <div style={baseStyles.radioOption}>
                        <input
                          type="radio"
                          id="female"
                          name="gender"
                          value="female"
                          checked={inputs.gender === 'female'}
                          onChange={(e) => setInputs({ ...inputs, gender: e.target.value as 'male' | 'female' })}
                          style={{ position: 'absolute', opacity: 0 }}
                        />
                        <label
                          htmlFor="female"
                          style={{
                            ...baseStyles.radioLabel,
                            ...(inputs.gender === 'female' ? baseStyles.radioLabelActive : {}),
                          }}
                        >
                          {t('genderFemale')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {unitSystem === 'imperial' ? (
                  <>
                    <div style={baseStyles.formGroup}>
                      <label style={baseStyles.label}>{t('labelHeight')}</label>
                      <div style={baseStyles.heightInputs}>
                        <div style={baseStyles.inputWithUnit}>
                          <input
                            type="number"
                            style={baseStyles.input}
                            min="3"
                            max="8"
                            value={inputs.feet}
                            onChange={(e) => setInputs({ ...inputs, feet: parseInt(e.target.value) })}
                            required
                          />
                          <span style={baseStyles.inputUnit}>{t('unitFeet')}</span>
                        </div>
                        <div style={baseStyles.inputWithUnit}>
                          <input
                            type="number"
                            style={baseStyles.input}
                            min="0"
                            max="11"
                            value={inputs.inches}
                            onChange={(e) => setInputs({ ...inputs, inches: parseInt(e.target.value) })}
                            required
                          />
                          <span style={baseStyles.inputUnit}>{t('unitInches')}</span>
                        </div>
                      </div>
                    </div>

                    <div style={baseStyles.formGroup}>
                      <label style={baseStyles.label}>{t('labelWeight')}</label>
                      <div style={baseStyles.inputWithUnit}>
                        <input
                          type="number"
                          style={baseStyles.input}
                          min="50"
                          max="500"
                          step="0.1"
                          value={inputs.weightLbs}
                          onChange={(e) => setInputs({ ...inputs, weightLbs: parseFloat(e.target.value) })}
                          required
                        />
                        <span style={baseStyles.inputUnit}>{t('unitLbs')}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={baseStyles.formGroup}>
                      <label style={baseStyles.label}>{t('labelHeight')}</label>
                      <div style={baseStyles.inputWithUnit}>
                        <input
                          type="number"
                          style={baseStyles.input}
                          min="100"
                          max="250"
                          step="0.1"
                          value={inputs.heightCm}
                          onChange={(e) => setInputs({ ...inputs, heightCm: parseFloat(e.target.value) })}
                          required
                        />
                        <span style={baseStyles.inputUnit}>{t('unitCm')}</span>
                      </div>
                    </div>

                    <div style={baseStyles.formGroup}>
                      <label style={baseStyles.label}>{t('labelWeight')}</label>
                      <div style={baseStyles.inputWithUnit}>
                        <input
                          type="number"
                          style={baseStyles.input}
                          min="20"
                          max="300"
                          step="0.1"
                          value={inputs.weightKg}
                          onChange={(e) => setInputs({ ...inputs, weightKg: parseFloat(e.target.value) })}
                          required
                        />
                        <span style={baseStyles.inputUnit}>{t('unitKg')}</span>
                      </div>
                    </div>
                  </>
                )}

                <div style={baseStyles.advancedSettings}>
                  <button
                    type="button"
                    style={baseStyles.settingsToggle}
                    onClick={() => setShowAdvanced(!showAdvanced)}
                  >
                    <span>⚙️</span> {t('advancedSettings')}
                  </button>
                  {showAdvanced && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={baseStyles.formGroup}>
                        <label style={baseStyles.label}>{t('labelFormula')}</label>
                        <select
                          style={baseStyles.input}
                          value={inputs.formula}
                          onChange={(e) => setInputs({ ...inputs, formula: e.target.value as 'mifflin' | 'harris' | 'katch' })}
                        >
                          <option value="mifflin">{t('formulaMifflin')}</option>
                          <option value="harris">{t('formulaHarris')}</option>
                          <option value="katch">{t('formulaKatch')}</option>
                        </select>
                      </div>

                      {inputs.formula === 'katch' && (
                        <div style={baseStyles.formGroup}>
                          <label style={baseStyles.label}>{t('labelBodyfat')}</label>
                          <div style={baseStyles.inputWithUnit}>
                            <input
                              type="number"
                              style={baseStyles.input}
                              min="5"
                              max="50"
                              step="0.1"
                              value={inputs.bodyfat}
                              onChange={(e) => setInputs({ ...inputs, bodyfat: parseFloat(e.target.value) })}
                            />
                            <span style={baseStyles.inputUnit}>%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  style={baseStyles.calculateButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(255, 90, 112, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={{ ...baseStyles.glassCard, padding: '2rem' }}>
                <h3 style={baseStyles.h3}>{t('resultsTitle')}</h3>

                <div style={baseStyles.bmrResult}>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                    {t('bmrLabel')}
                  </div>
                  <div style={bmrValueStyle}>{results.bmr.toLocaleString()}</div>
                  <div style={{ fontSize: '1rem', opacity: 0.9 }}>{t('caloriesPerDay')}</div>
                </div>

                <h4 style={{ margin: '1.5rem 0 0.75rem 0', color: '#334155', fontSize: '1rem' }}>
                  {t('tdeeTitle')}
                </h4>

                <table style={baseStyles.tdeeTable}>
                  <thead>
                    <tr>
                      <th style={baseStyles.tdeeTableTh}>{t('activityLevel')}</th>
                      <th style={{ ...baseStyles.tdeeTableTh, textAlign: 'right' }}>{t('caloriesPerDay')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.tdee.map((item, index) => (
                      <tr key={index}>
                        <td style={{ ...baseStyles.tdeeTableTd, fontWeight: 500 }}>{item.level}</td>
                        <td style={{ ...baseStyles.tdeeTableTd, textAlign: 'right', fontWeight: 600, color: '#2563eb' }}>
                          {item.calories.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={baseStyles.infoNote}>
                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#92400e' }}>
                    💡 {t('noteTitle')}
                  </strong>
                  {t('noteText')}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('howToStep1')}</li>
          <li style={baseStyles.listItem}>{t('howToStep2')}</li>
          <li style={baseStyles.listItem}>{t('howToStep3')}</li>
          <li style={baseStyles.listItem}>{t('howToStep4')}</li>
          <li style={baseStyles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('insight1')}</li>
          <li style={baseStyles.listItem}>{t('insight2')}</li>
          <li style={baseStyles.listItem}>{t('insight3')}</li>
          <li style={baseStyles.listItem}>{t('insight4')}</li>
          <li style={baseStyles.listItem}>{t('insight5')}</li>
          <li style={baseStyles.listItem}>{t('insight6')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={baseStyles.h3}>{t('detailsFormulasTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsFormulas')}</p>

        <h3 style={baseStyles.h3}>{t('detailsActivityTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsActivity')}</p>

        <h3 style={baseStyles.h3}>{t('detailsWeightTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsWeight')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={baseStyles.reference}>
          <a
            href="https://www.vanswefitness.com/pages/bmr-calculator-to-lose-weight"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceLink}
          >
            BMR Calculator to Lose Weight - Vanswe Fitness
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a
            href="https://cfxfit.com/fitness-insights/what-is-basal-metabolic-rate-bmr/"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceLink}
          >
            What is Basal Metabolic Rate (BMR)? - CFX Fit
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a
            href="https://www.womenshealthmag.com/fitness/a40643747/what-is-bmr/"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceLink}
          >
            What Is BMR (Basal Metabolic Rate) - Women's Health
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a
            href="https://www.healthline.com/health/what-is-basal-metabolic-rate"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceLink}
          >
            What Is Basal Metabolic Rate? - Healthline
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a
            href="https://www.garnethealth.org/news/basal-metabolic-rate-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceLink}
          >
            Basal Metabolic Rate Calculator - Garnet Health
          </a>
        </div>
      </section>
    </div>
  );
}